import { useEffect, useMemo, useState } from "react";
import ModalComponent from "components/modal";
import { useSharedState } from "state";
import Table from "components/table";
import ActionButton from "components/table/action-btn";
import { oneLine } from "components/table/table-parts";
import { IconCheck, IconLock, IconLockOpen, IconPencil, IconX } from "@tabler/icons-react";
import WarningModal from "components/modal/warning";
import { addCompany, updateCompany } from "api/companies";
import { CompanyDefault, CompanyType, LocationType } from "types/dict";
import { getLocationsByUser } from "api/location";

export default function Companies() {
  const { companies, getCompaniesData, notify } = useSharedState();

  const [userLocations, setUserLocations] = useState<LocationType[]>([]);
  const [companyToChange, setCompanyToChange] = useState<CompanyType>(CompanyDefault);
  const [editingCompany, setEditingCompany] = useState<number>(0);
  const [isAddingCompany, setAddingCompany] = useState<boolean>(false);
  const [isFormLoading, setFormLoading] = useState<boolean>(false);

  const [newCompanyName, setNewCompanyName] = useState<string>("");
  const [newCompanyBin, setNewCompanyBin] = useState<string>("");
  const [newCompanyLocation, setNewCompanyLocation] = useState<string>("");

  const changeCompanyDanger = "Вы действительно хотите отредактировать юридическое лицо?";

  const locationOptions = useMemo(() => {
    return [
      { value: "", label: "Выберите филиал", disabled: true },
      ...userLocations
        .filter((loc) => loc.status)
        .map((loc) => ({ value: loc.location_id, label: loc.location, disabled: false })),
    ];
  }, [userLocations]);

  useEffect(() => {
    getCompaniesData();
    getUserLocations();
  }, []);

  const openCompanyModal = () => {
    const button = document.createElement("button");
    button.setAttribute("data-bs-toggle", "modal");
    button.setAttribute("data-bs-target", "#modal");
    document.body.appendChild(button);
    button.click();
    button.remove();
  };

  const resetInputs = () => {
    setNewCompanyName("");
    setNewCompanyBin("");
    setNewCompanyLocation("");
  };

  const cancelCompanyCreation = () => {
    setCompanyToChange(CompanyDefault);
    setEditingCompany(0);
    setAddingCompany(false);

    resetInputs();
  };

  const onCompanyEdit = (item: CompanyType) => {
    setEditingCompany(item.company_id);
    setCompanyToChange(item);
    setAddingCompany(false);

    setNewCompanyName(item.company);
    setNewCompanyBin(item.bin);
    setNewCompanyLocation(item.location_id.toString());
  };

  const onCompanyBlock = (item: CompanyType) => {
    setCompanyToChange(item);
    setEditingCompany(0);
    setAddingCompany(false);

    resetInputs();
  };

  const onCompanyCreate = () => {
    setEditingCompany(0);
    setAddingCompany(true);

    resetInputs();
  };

  const getUserLocations = async () => {
    try {
      const userLocationsData: LocationType[] = await getLocationsByUser();
      setUserLocations(userLocationsData);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении филиалов");
    }
  };

  const handleBinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let newValue = value;

    // Only allow numbers and max 12 digits
    newValue = newValue.replace(/\D/g, "").slice(0, 12);

    setNewCompanyBin(newValue);
  };

  const changeCompany = async () => {
    openCompanyModal();
    setFormLoading(true);

    try {
      await updateCompany(
        companyToChange.company_id,
        newCompanyName,
        Number(newCompanyLocation),
        newCompanyBin,
        companyToChange.status
      );
      notify("Юр. лицо изменено", "success");

      cancelCompanyCreation();
      await getCompaniesData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при изменении юр. лица");
    } finally {
      setFormLoading(false);
    }
  };

  const changeCompanyStatus = async () => {
    openCompanyModal();
    setFormLoading(true);

    try {
      await updateCompany(
        companyToChange.company_id,
        companyToChange.company,
        companyToChange.location_id,
        companyToChange.bin,
        !companyToChange.status
      );
      notify("Статус изменён", "success");

      cancelCompanyCreation();
      await getCompaniesData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при изменении статуса");
    } finally {
      setFormLoading(false);
    }
  };

  const createCompany = async () => {
    setFormLoading(true);

    try {
      await addCompany(newCompanyName, Number(newCompanyLocation), newCompanyBin);
      notify("Юр. лицо добавлено", "success");

      cancelCompanyCreation();
      await getCompaniesData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при добавлении юр. лица");
    } finally {
      setFormLoading(false);
    }
  };

  const companiesTableHeaders = [
    { header_text: "Юр. лицо", narrow: false, wrap: false },
    { header_text: "БИН", narrow: false, wrap: false },
    { header_text: "Филиал", narrow: false, wrap: false },
    { header_text: "Статус", narrow: false, wrap: false },
    { header_text: "Действия", narrow: false, wrap: false },
  ];

  const companiesList = (
    <>
      {companies.map((company, index) => (
        <tr key={index}>
          {editingCompany === company.company_id ? (
            <>
              <td>
                <input
                  type="text"
                  className="form-control rounded-2xl"
                  placeholder="Имя юр. лица"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value.slice(0, 100))}
                  disabled={isFormLoading}
                />
              </td>
              <td>
                <input
                  type="number"
                  className="form-control rounded-2xl"
                  placeholder="БИН"
                  value={newCompanyBin}
                  onChange={handleBinChange}
                  disabled={isFormLoading}
                />
              </td>
              <td>
                <select
                  className="form-select rounded-2xl"
                  value={newCompanyLocation}
                  onChange={(e) => setNewCompanyLocation(e.target.value)}
                  disabled={isFormLoading}
                >
                  {locationOptions.map((opt, index) => (
                    <option key={index} value={opt.value} disabled={opt.disabled}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </td>
              <td></td>
              <td>
                <div className="btn-list flex-nowrap">
                  <ActionButton
                    icon={<IconCheck className="icon" />}
                    title="Сохранить"
                    action={() => undefined}
                    color="primary"
                    modal={true}
                    modal_id="#modal-change-company"
                  />
                  <ActionButton
                    icon={<IconX className="icon" />}
                    title="Отмена"
                    action={cancelCompanyCreation}
                    color="danger"
                    modal={false}
                  />
                </div>
              </td>
            </>
          ) : (
            <>
              <td>{oneLine(company.company || "-")}</td>
              <td>{oneLine(company.bin || "-")}</td>
              <td>{oneLine(userLocations.find((loc) => loc.location_id === company.location_id)?.location || "-")}</td>
              <td>{oneLine(company.status ? "Активный" : "Неактивный", company.status ? "primary" : "danger")}</td>
              <td>
                <div className="btn-list flex-nowrap">
                  <ActionButton
                    icon={<IconPencil className="icon" />}
                    title={"Изменить"}
                    action={() => onCompanyEdit(company)}
                    color={"primary"}
                    modal={false}
                  />
                  <ActionButton
                    icon={company.status ? <IconLock className="icon" /> : <IconLockOpen className="icon" />}
                    title={`${company.status ? "Деактивировать" : "Активировать"}`}
                    action={() => onCompanyBlock(company)}
                    color={`${company.status ? "danger" : "primary"}`}
                    modal={true}
                    modal_id="#modal-change-company"
                  />
                </div>
              </td>
            </>
          )}
        </tr>
      ))}

      {isAddingCompany && (
        <tr>
          <td>
            <input
              type="text"
              className="form-control rounded-2xl"
              placeholder="Имя юр. лица"
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value.slice(0, 100))}
              disabled={isFormLoading}
            />
          </td>
          <td>
            <input
              type="number"
              className="form-control rounded-2xl"
              placeholder="БИН"
              value={newCompanyBin}
              onChange={handleBinChange}
              disabled={isFormLoading}
            />
          </td>
          <td>
            <select
              className="form-select rounded-2xl"
              value={newCompanyLocation}
              onChange={(e) => setNewCompanyLocation(e.target.value)}
              disabled={isFormLoading}
            >
              {locationOptions.map((opt, index) => (
                <option key={index} value={opt.value} disabled={opt.disabled}>
                  {opt.label}
                </option>
              ))}
            </select>
          </td>
          <td></td>
          <td>
            <ActionButton
              icon={<IconX className="icon" />}
              title="Отмена"
              action={cancelCompanyCreation}
              color="danger"
              modal={false}
            />
          </td>
        </tr>
      )}
    </>
  );

  const usersTable = Table(companiesTableHeaders, companiesList, false);

  const companiesFooter = (
    <button
      className="btn btn-primary d-flex gap-1"
      onClick={isAddingCompany ? createCompany : onCompanyCreate}
      disabled={isFormLoading || (isAddingCompany && !newCompanyName?.length)}
    >
      {isAddingCompany ? "Сохранить" : "Добавить юр. лицо"}
      {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
    </button>
  );

  return (
    <>
      <ModalComponent
        header="Список юридических лиц"
        body={usersTable}
        footer={companiesFooter}
        disableClose={isFormLoading}
      />
      <WarningModal
        modal_id="modal-change-company"
        warningDescription={changeCompanyDanger}
        cancel={openCompanyModal}
        confirm={editingCompany ? changeCompany : changeCompanyStatus}
      />
    </>
  );
}
